import type { ApolloFilters } from "../lib/types";
import { TagInput } from "./TagInput";

interface Props {
  value: ApolloFilters;
  onChange: (next: ApolloFilters) => void;
}

const ARRAY_KEYS = [
  "titles",
  "seniorities",
  "regions",
  "organization_keywords",
] as const;
type ArrayKey = (typeof ARRAY_KEYS)[number];

const NUMBER_KEYS = [
  "min_employees",
  "max_employees",
  "min_revenue",
  "max_revenue",
] as const;
type NumberKey = (typeof NUMBER_KEYS)[number];

function asStringArray(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v.filter((x): x is string => typeof x === "string");
}

function asNumberOrEmpty(v: unknown): string {
  if (typeof v === "number" && Number.isFinite(v)) return String(v);
  return "";
}

/**
 * Structured editor for ``icps.search_filters``. Renders known fields as
 * tag inputs (arrays) and number inputs (ranges); unknown keys are passed
 * through unchanged so adding a new Apollo field manually still works.
 */
export function ApolloFiltersEditor({ value, onChange }: Props) {
  const setArray = (key: ArrayKey, next: string[]) => {
    onChange({ ...value, [key]: next });
  };

  const setNumber = (key: NumberKey, raw: string) => {
    if (raw === "") {
      const { [key]: _drop, ...rest } = value;
      onChange(rest);
      return;
    }
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return;
    onChange({ ...value, [key]: parsed });
  };

  return (
    <div className="apollo-filters">
      <div className="field">
        <label>Job titles</label>
        <TagInput
          values={asStringArray(value.titles)}
          onChange={(next) => setArray("titles", next)}
          placeholder="e.g. CEO, Chief Executive Officer, Founder…"
        />
      </div>

      <div className="field">
        <label>Seniorities</label>
        <TagInput
          values={asStringArray(value.seniorities)}
          onChange={(next) => setArray("seniorities", next)}
          placeholder="e.g. c_suite, founder, owner, vp, head, director…"
        />
      </div>

      <div className="field">
        <label>Regions (HQ country)</label>
        <TagInput
          values={asStringArray(value.regions)}
          onChange={(next) => setArray("regions", next)}
          placeholder="e.g. Germany, Austria, United Kingdom…"
        />
      </div>

      <div className="field">
        <label>
          Organization keywords{" "}
          <span
            className="field-help"
            title="Apollo matches an org if it has ANY of these keywords. Prefer specific terms — broad ones (e.g. 'software', 'tech') pull in companies outside the ICP."
          >
            ⓘ
          </span>
        </label>
        <TagInput
          values={asStringArray(value.organization_keywords)}
          onChange={(next) => setArray("organization_keywords", next)}
          placeholder="e.g. accounting, ERP, bookkeeping…"
          maxValues={15}
        />
      </div>

      <div className="field-row">
        <div className="field">
          <label htmlFor="af-min-emp">Min employees</label>
          <input
            id="af-min-emp"
            type="number"
            min={0}
            value={asNumberOrEmpty(value.min_employees)}
            onChange={(e) => setNumber("min_employees", e.target.value)}
            placeholder="e.g. 30"
          />
        </div>
        <div className="field">
          <label htmlFor="af-max-emp">Max employees</label>
          <input
            id="af-max-emp"
            type="number"
            min={0}
            value={asNumberOrEmpty(value.max_employees)}
            onChange={(e) => setNumber("max_employees", e.target.value)}
            placeholder="e.g. 800"
          />
        </div>
      </div>

      <div className="field-row">
        <div className="field">
          <label htmlFor="af-min-rev">Min revenue (USD)</label>
          <input
            id="af-min-rev"
            type="number"
            min={0}
            value={asNumberOrEmpty(value.min_revenue)}
            onChange={(e) => setNumber("min_revenue", e.target.value)}
            placeholder="e.g. 2000000"
          />
        </div>
        <div className="field">
          <label htmlFor="af-max-rev">Max revenue (USD)</label>
          <input
            id="af-max-rev"
            type="number"
            min={0}
            value={asNumberOrEmpty(value.max_revenue)}
            onChange={(e) => setNumber("max_revenue", e.target.value)}
            placeholder="e.g. 110000000"
          />
        </div>
      </div>
    </div>
  );
}
